import { useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle2, FileSignature, HardHat } from 'lucide-react';
import { APP_NAME } from '@helfr/domain';
import { api, hlaskaZChyby } from '../api/client.js';
import { PodpisPad } from '../components/PodpisPad.js';
import { Btn, Chyba, Input, Nacitani, Pole } from '../components/ui.js';

interface OdkazOdpoved {
  dokument: {
    typ: string;
    cislo: string;
    popis: string;
    kontext: string;
    smer: string | null;
    podpisovaCastkaText: string;
    firmaNazev: string;
  };
  jmeno: string | null;
  expiresAt: string;
  rezim?: 'handoff' | 'protistrana';
}

/**
 * VEŘEJNÁ podpisová stránka (/podpis/:token) — protistrana (subka, TDI)
 * podepíše dokument bez registrace. Vidí jen ručně sestavené shrnutí SVÉHO
 * dokumentu (finanční zeď drží server), podepíše prstem/myší, hotovo.
 */
export function VerejnyPodpisPage() {
  const { token } = useParams<{ token: string }>();
  const [jmeno, setJmeno] = useState('');
  const [obrazek, setObrazek] = useState<string | null>(null);
  const [hotovo, setHotovo] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['verejny-podpis', token],
    queryFn: () => api.get<OdkazOdpoved>(`/api/podpis/${token}`),
    retry: false,
  });

  const d = data?.dokument;
  const vychoziJmeno = data?.jmeno ?? '';
  const zobrazeneJmeno = jmeno || vychoziJmeno;

  const podepsat = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean }>(`/api/podpis/${token}`, {
        podpis: { obrazek, jmeno: zobrazeneJmeno.trim() },
      }),
    onSuccess: () => setHotovo(true),
  });

  return (
    <main className="min-h-screen bg-bg">
      {/* Hlavička: čí dokument podepisuji. */}
      <header className="bg-primary px-4 py-3 text-white">
        <div className="mx-auto flex max-w-xl items-center gap-2.5">
          <span className="flex size-8 items-center justify-center rounded-lg bg-accent text-text-heading">
            <HardHat size={18} strokeWidth={2} aria-hidden="true" />
          </span>
          <span className="font-bold tracking-tight">{APP_NAME}</span>
          {d && <span className="ml-auto truncate text-sm text-white/80">{d.firmaNazev}</span>}
        </div>
      </header>

      <div className="mx-auto flex max-w-xl flex-col gap-4 p-4 py-6">
        {isLoading && <Nacitani text="Načítám dokument…" />}

        {error && (
          <div className="rounded-xl border border-border bg-surface p-6 text-center">
            <p className="text-lg font-bold">Odkaz nefunguje</p>
            <p className="mt-1 text-base text-text-secondary">{hlaskaZChyby(error)}</p>
          </div>
        )}

        {d && hotovo && (
          <div className="flex flex-col items-center gap-2 rounded-xl border border-success-border bg-success-bg p-8 text-center">
            <CheckCircle2 size={40} strokeWidth={1.75} className="text-success-text" />
            <p className="text-xl font-bold text-success-text">Podepsáno</p>
            <p className="text-base text-success-text">
              {data?.rezim === 'handoff'
                ? 'Podpis se přenesl do počítače — dokončete akci tam. Tuto stránku můžete zavřít.'
                : `Podpis je zaznamenaný u dokumentu ${d.typ.toLowerCase()} ${d.cislo}. Tuto stránku můžete zavřít.`}
            </p>
          </div>
        )}

        {d && !hotovo && (
          <>
            {/* Co přesně podepisuji — dokument jako karta. */}
            <section className="rounded-xl border-2 border-primary bg-surface p-4">
              <p className="flex items-center gap-2 text-xs font-semibold tracking-wider text-text-secondary uppercase">
                <FileSignature size={14} strokeWidth={2} aria-hidden="true" />
                {d.typ} {d.cislo}
              </p>
              <h1 className="mt-1 text-xl font-bold">{d.popis}</h1>
              <p className="text-sm text-text-secondary">{d.kontext}</p>
              {d.smer && <p className="mt-1 text-sm text-text-secondary">Směr: {d.smer}</p>}
              <p className="mt-3 border-t border-border pt-3 text-2xl font-bold tabular-nums">
                {d.podpisovaCastkaText}
              </p>
            </section>

            <section className="flex flex-col gap-4 rounded-xl border border-border bg-surface p-4">
              <Pole popisek="Jméno a příjmení podepisujícího" povinne>
                <Input required value={zobrazeneJmeno} onChange={(e) => setJmeno(e.target.value)} />
              </Pole>
              <PodpisPad onChange={setObrazek} />
              {podepsat.error ? <Chyba>{hlaskaZChyby(podepsat.error)}</Chyba> : null}
              <Btn
                className="w-full"
                disabled={!obrazek || zobrazeneJmeno.trim().length < 2 || podepsat.isPending}
                onClick={() => podepsat.mutate()}
              >
                {podepsat.isPending ? 'Odesílám…' : 'Podepsat dokument'}
              </Btn>
              <p className="text-center text-sm text-text-secondary">
                Podpis se uloží s časem a IP adresou jako doklad o podepsání.
              </p>
            </section>
          </>
        )}

        {/* Decentní stopka pro protistranu — jediný akviziční kanál, který se
            šíří prací samou (subka je často sama GD pro své subky). Jen fakta. */}
        <footer className="mt-8 border-t border-border pt-4 pb-6 text-center text-sm text-text-secondary">
          <p>
            Dokument vznikl v aplikaci <strong>{APP_NAME}</strong> — stavební deník a hlídač
            víceprací.
          </p>
          <a
            href="/registrace"
            className="mt-1 inline-block font-semibold text-primary underline underline-offset-2"
          >
            Chci to pro svou firmu — účet zdarma →
          </a>
        </footer>
      </div>
    </main>
  );
}
